import { useState, useMemo } from 'react';
import moment from "moment-timezone";
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Combobox } from '@/components/ui/combobox';
import { useToast } from '@/components/ui/use-toast';
import { useListCustomers } from '@/lib/api/customer';
import { useUpsertOrderMetadata, TtOrderMetadata } from '@/lib/api/risk/brokerage/tt';
import { SelectOptionItem } from '@/lib/models/ui';
import { Badge } from '@/components/ui/badge';
import { cn, CommonDestinationTimezone } from '@/lib/utils';
import { guessOrderTypeFromText } from '../transform';

type OrderType = 'hta' | 'pricing' | 'position_adjust' | 'cash' | 'otc' | 'none';

type ValueSource = 'current' | 'suggested' | 'updated';

interface SuggestedValues {
  customerId?: string;
  orderType?: OrderType;
}

interface OrderTextFields {
  textA?: string | null;
  textB?: string | null;
  textC?: string | null;
  textTT?: string | null;
}

interface CustomerOption {
  id: string;
  longName: string;
}

interface AttachCustomerFormProps {
  guidOrderId: string;
  existingMetadata?: TtOrderMetadata | null;
  onSuccess?: () => void;
  isInline?: boolean;
  isModal?: boolean;
  orderTextFields?: OrderTextFields;
}

/**
 * Suggests a customer ID by matching tokens from text fields against customer long names.
 * Splits the combined text fields by spaces and counts how many tokens appear in each customer's long name.
 * Also suggests order type based on text field patterns.
 */
function getSuggestedValues(
  orderTextFields: OrderTextFields | undefined,
  customers: CustomerOption[]
): SuggestedValues {
  const result: SuggestedValues = {};

  // Suggest order type from text patterns using transform util
  if (orderTextFields) {
    const guessed = guessOrderTypeFromText(
      orderTextFields.textA ?? null,
      orderTextFields.textB ?? null,
      orderTextFields.textC ?? null,
      orderTextFields.textTT ?? null
    );
    if (guessed) {
      result.orderType = guessed;
    }
  }

  if (!orderTextFields || customers.length === 0) {
    return result;
  }

  const { textA, textB, textC, textTT } = orderTextFields;
  
  // Combine all text fields and split by spaces
  const combinedText = [textA, textB, textC, textTT]
    .filter((t): t is string => !!t)
    .join(' ');
  
  if (!combinedText.trim()) {
    return result;
  }

  // Split into tokens and normalize (lowercase, remove empty strings)
  const tokens = combinedText
    .split(/\s+/)
    .map(t => t.toLowerCase().trim())
    .filter(t => t.length > 0);

  if (tokens.length === 0) {
    return result;
  }

  // Score each customer by counting matching tokens
  const scoredCustomers = customers.map(customer => {
    const longNameLower = customer.longName.toLowerCase();
    const matchingTokens = tokens.filter(token => longNameLower.includes(token));
    return {
      customer,
      matchCount: matchingTokens.length,
      matchingTokens,
    };
  });

  // Filter to customers with at least one match
  const customersWithMatches = scoredCustomers.filter(sc => sc.matchCount > 0);

  if (customersWithMatches.length === 0) {
    return result;
  }

  // Sort by match count descending
  customersWithMatches.sort((a, b) => b.matchCount - a.matchCount);

  const bestMatch = customersWithMatches[0];

  result.customerId = bestMatch.customer.id;
  return result;
}

function ValueSourceBadge({ source }: { source: ValueSource }) {
  const config = {
    current: { label: 'Current', className: 'bg-muted text-muted-foreground' },
    suggested: { label: 'Suggested', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
    updated: { label: 'Updated', className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  };
  const { label, className } = config[source];
  return <Badge variant="outline" className={cn('ml-2 text-xs font-normal', className)}>{label}</Badge>;
}

function getOrderTypeLabel(orderType: OrderType): string {
  const labels: Record<OrderType, string> = {
    none: 'None',
    hta: 'HTA',
    pricing: 'Pricing',
    position_adjust: 'Position Adjust',
    cash: 'Cash',
    otc: 'OTC',
  };
  return labels[orderType];
}

export function AttachCustomerForm({
  guidOrderId,
  existingMetadata,
  onSuccess,
  isInline = false,
  isModal = false,
  orderTextFields,
}: AttachCustomerFormProps) {
  const { toast } = useToast();
  const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const upsertMetadata = useUpsertOrderMetadata();

  // Build customer options for both dropdown and suggestion matching
  const customerOptionsForMatching: CustomerOption[] = useMemo(() => {
    const customers = customersResult.data?.data ?? [];
    return customers.map((customer: { id: string; short_name?: string; long_name?: string }) => ({
      id: customer.id,
      longName: customer.long_name || customer.short_name || customer.id,
    }));
  }, [customersResult.data?.data]);

  const suggestedValues = useMemo(
    () => getSuggestedValues(orderTextFields, customerOptionsForMatching),
    [orderTextFields, customerOptionsForMatching]
  );

  // Determine existing order type from metadata (what's saved)
  const getExistingOrderType = (): OrderType => {
    if (!existingMetadata) return 'none';
    if (existingMetadata.expected_hta) return 'hta';
    if (existingMetadata.expected_pricing) return 'pricing';
    if (existingMetadata.expected_position_adjust) return 'position_adjust';
    if (existingMetadata.expected_cash) return 'cash';
    if (existingMetadata.expected_otc) return 'otc';
    return 'none';
  };

  const existingOrderType = getExistingOrderType();
  const existingCustomerId = existingMetadata?.specified_customer_id ?? '';

  // Initialize with suggested value if no existing value
  const getInitialCustomerId = (): string => {
    if (existingCustomerId) return existingCustomerId;
    if (suggestedValues.customerId) return suggestedValues.customerId;
    return '';
  };

  // Initialize order type: use existing if set, otherwise use suggested
  const getInitialOrderType = (): OrderType => {
    if (existingOrderType !== 'none') return existingOrderType;
    if (suggestedValues.orderType) return suggestedValues.orderType;
    return 'none';
  };

  const currentYear = moment.tz(CommonDestinationTimezone).format("YYYY");

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(getInitialCustomerId());
  const [selectedCropYear, setSelectedCropYear] = useState<string>(existingMetadata?.crop_year !== 0 ? existingMetadata?.crop_year?.toString() ?? currentYear : currentYear);
  const [orderType, setOrderType] = useState<OrderType>(getInitialOrderType());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const customerOptions: SelectOptionItem<string>[] = useMemo(() => {
    const customers = customersResult.data?.data ?? [];
    return customers.map((customer: { id: string; short_name?: string; long_name?: string }) => ({
      value: customer.id,
      label: customer.long_name || customer.short_name || customer.id,
    }));
  }, [customersResult.data?.data]);

  const getCustomerName = (customerId: string): string => {
    const option = customerOptions.find(opt => opt.value === customerId);
    return option?.label ?? customerId;
  };

  // Determine the source of the current customer value
  const getCustomerValueSource = (): ValueSource => {
    if (existingCustomerId && selectedCustomerId !== existingCustomerId) return 'updated';
    if (existingCustomerId && selectedCustomerId === existingCustomerId) return 'current';
    if (!existingCustomerId && suggestedValues.customerId && selectedCustomerId === suggestedValues.customerId) return 'suggested';
    if (!existingCustomerId && selectedCustomerId) return 'updated';
    return 'current';
  };

  // Determine the source of the order type value
  const getOrderTypeValueSource = (): ValueSource => {
    // If there's existing metadata with a real order type set
    if (existingOrderType !== 'none') {
      if (orderType !== existingOrderType) return 'updated';
      return 'current';
    }
    // No existing order type - check if using suggested
    if (suggestedValues.orderType && orderType === suggestedValues.orderType) return 'suggested';
    if (orderType !== 'none') return 'updated';
    return 'current';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!selectedCustomerId) {
      toast({
        title: 'Validation Error',
        description: 'Please select a customer',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedCropYear) {
      toast({
        title: 'Validation Error',
        description: 'Please select a crop year',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await upsertMetadata.mutateAsync({
        guid_order_id: guidOrderId,
        expected_hta: orderType === 'hta',
        expected_pricing: orderType === 'pricing',
        expected_position_adjust: orderType === 'position_adjust',
        expected_cash: orderType === 'cash',
        expected_otc: orderType === 'otc',

        specified_customer_id: selectedCustomerId,
        specified_customer_split_id: undefined,

        split_type: undefined,
        split_percentage_a: undefined,
        split_percentage_b: undefined,

        crop_year: parseInt(selectedCropYear),
      });

      if (result.success) {
        toast({
          title: 'Success',
          description: 'Order metadata updated successfully',
        });
        onSuccess?.();
      } else {
        throw new Error(result.message || 'Failed to update metadata');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update metadata';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasExistingMetadata = !!existingMetadata;

  const hasTextFields = orderTextFields && (
    orderTextFields.textA || orderTextFields.textB || orderTextFields.textC || orderTextFields.textTT
  );


  const cropYearOptions: SelectOptionItem<string>[] = Array.from({ length: 10 }, (_, i) => {
      const year = parseInt(currentYear) - i + 3;
      return {
          value: year.toString(),
          label: year.toString()
      };
  });

  return (
    <form onSubmit={handleSubmit} className={isInline ? 'space-y-4' : 'space-y-4 py-4'}>
      {/* Order Text Fields Summary - only shown in modal */}
      {isModal && hasTextFields && (
        <div className="p-3 bg-muted/50 rounded-lg border text-sm">
          <div className="grid grid-cols-4 gap-x-4 gap-y-1">
            {orderTextFields.textA && (
              <span className="grid">
                <span className="text-muted-foreground">Text A:</span>
                <span className="font-medium">{orderTextFields.textA}</span>
              </span>
            )}
            {orderTextFields.textB && (
              <span className="grid">
                <span className="text-muted-foreground">Text B:</span>
                <span className="font-medium">{orderTextFields.textB}</span>
              </span>
            )}
            {orderTextFields.textC && (
              <span className="grid">
                <span className="text-muted-foreground">Text C:</span>
                <span className="font-medium">{orderTextFields.textC}</span>
              </span>
            )}
            {orderTextFields.textTT && (
              <span className="grid">
                <span className="text-muted-foreground">Text TT:</span>
                <span className="font-medium">{orderTextFields.textTT}</span>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column: Existing State (Read-only) */}
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            Existing Metadata
          </h3>
          {hasExistingMetadata ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Customer</Label>
                <p className="text-sm font-medium">
                  {existingCustomerId ? getCustomerName(existingCustomerId) : <span className="text-muted-foreground italic">Not set</span>}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Order Type</Label>
                <p className="text-sm font-medium">{getOrderTypeLabel(existingOrderType)}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Crop Year</Label>
                <p className="text-sm font-medium">{existingMetadata?.crop_year ?? ""}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-24 text-muted-foreground italic text-sm">
              No existing metadata
            </div>
          )}
        </div>

        {/* Right Column: Editable Form */}
        <div className="space-y-4">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            {hasExistingMetadata ? 'Update Metadata' : 'Set Metadata'}
          </h3>

          <div className="space-y-2 grid">
            <div className="flex items-center">
              <Label>Customer <span className="text-destructive">*</span></Label>
              {selectedCustomerId && <ValueSourceBadge source={getCustomerValueSource()} />}
            </div>
            <Combobox
              dwimOnChange
              isModal={isModal}
              placeholder="Select customer..."
              options={customerOptions}
              value={selectedCustomerId}
              onValueChange={setSelectedCustomerId}
              disabled={customersResult.isLoading}
              virtualizeList
              popoverClassName="w-[400px] h-[200px]"
            />
          </div>

          <div className="space-y-2 grid">
            <div className="flex items-center">
              <Label>Crop Year <span className="text-destructive">*</span></Label>
            </div>
            <Combobox
              dwimOnChange
              isModal={isModal}
              placeholder="Select crop year..."
              options={cropYearOptions}
              value={selectedCropYear}
              onValueChange={setSelectedCropYear}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <Label>Order Type</Label>
              {(orderType !== 'none' || existingOrderType !== 'none') && (
                <ValueSourceBadge source={getOrderTypeValueSource()} />
              )}
            </div>
            <RadioGroup
              value={orderType}
              onValueChange={(value: string) => setOrderType(value as OrderType)}
              className="flex flex-col space-y-1"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="type-none" />
                <Label htmlFor="type-none" className="font-normal cursor-pointer">
                  None
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hta" id="type-hta" />
                <Label htmlFor="type-hta" className="font-normal cursor-pointer">
                  HTA
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pricing" id="type-pricing" />
                <Label htmlFor="type-pricing" className="font-normal cursor-pointer">
                  Pricing
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="position_adjust" id="type-position-adjust" />
                <Label htmlFor="type-position-adjust" className="font-normal cursor-pointer">
                  Position Adjust
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="otc" id="type-otc" />
                <Label htmlFor="type-otc" className="font-normal cursor-pointer">
                  OTC
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </div>

      <div className={isInline ? 'flex justify-end' : 'flex justify-end'}>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
